/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import _ from "lodash";
import { PostType } from "api";

function Breadcrumbs({ icon, title, route, light }) {
  const routes = route.slice(0, -1);
  const loc = useLocation();
  const params = useParams();
  const { t, i18n } = useTranslation();
  const isPostType = (loc.pathname.match(/^\/post-types\/(\d+)\/posts/) || []).length === 2;
  const [postType, setPostType] = useState(undefined);

  useEffect(async () => {
    if (isPostType) {
      if (_.get(loc, 'state.postType')) {
        setPostType(loc.state.postType)
      } else {
        const { postTypeId } = params;
        const { data } = await PostType.get(postTypeId);
        setPostType(data);
      }
    }
  }, []);

  return (
    <SuiBox mr={{ xs: 0, xl: 8 }}>
      <SuiTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {t(title.replace("-", " "))}
      </SuiTypography>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        <Link to="/">
          <SuiTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </SuiTypography>
        </Link>
        {routes.map((el) => isPostType && el === 'posts' ? "" : (
          <Link to={`/${el}`} key={el}>
            <SuiTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {t(el.replace("-", " "))}
            </SuiTypography>
          </Link>
        ))}
        {isPostType &&
          <Link to={`/post-types/${(postType || {}).id}/posts`} state={{ postType }}>
            <SuiTypography
              component="span"
              variant="body2"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {_.get(postType, 'title' + String(i18n.language === "en" ? "" : i18n.language).toUpperCase())}
            </SuiTypography>
          </Link>
        }
        {(!isPostType || title === 'new' || title === 'edit') &&
          <SuiTypography
            variant="button"
            fontWeight="regular"
            textTransform="capitalize"
            color={light ? "white" : "dark"}
            sx={{ lineHeight: 0 }}
          >
            {t(title.replace("-", " "))}
          </SuiTypography>
        }
      </MuiBreadcrumbs>
    </SuiBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
